import { ClientProps } from '@/interfaces/models/client';
import { ClientChatProps } from '@/interfaces/models/clientChat';
import { IngredientProps } from '@/interfaces/models/ingredient';
import { TakeAwayProps } from '@/interfaces/models/order';
import { ProductProps } from '@/interfaces/models/product';
import { UserProps } from '@/interfaces/models/user';
import Client from '@/models/client';
import DeviceKassa from '@/models/deviceKassa';
import User from '@/models/user';
import { RootState } from '@/store';
import SystemDate from '@/utils/systemDate';
import { v4 } from 'uuid';
import { ActionContext, ActionTree, GetterTree, MutationTree } from 'vuex';
import {Promotion} from '@/consts/loyaltySystem/createPromotionPopup';

const namespaced: boolean = false;

export interface SocketState {
}

const state: SocketState = {};

const getters: GetterTree<SocketState, RootState> = {};

const actions: ActionTree<SocketState, RootState> = {
  socketCreateReceiptBySavedOrder({ commit, dispatch }: ActionContext<SocketState, RootState>, receiptProps: any) {
    try {
      dispatch('socketCreateReceipt', receiptProps);

      commit('pos/removeSavedReceipt', receiptProps.id);
    } catch (e) {
    }
  },
  socketIssueAndPayOrder({ commit, dispatch }: ActionContext<SocketState, RootState>, receiptProps: any) {
    dispatch('socketIssueOrder', { id: receiptProps.id });
    commit('pos/createReceipt', receiptProps);
  },
  socketAddClientToOrder({ commit }: ActionContext<SocketState, RootState>, receiptProps: any) {
    receiptProps.client = Client.createFromRequest(receiptProps.client);

    commit('clients/addClient', receiptProps.client);

    if (receiptProps.isSave) {
      commit('pos/addClientToSavedReceipt', receiptProps);
    } else {
      commit('pos/addClientToReceipt', receiptProps);
      commit('pos/addClientToActualOrder', receiptProps);
    }
  },
  socketRemoveClientFromOrder({ commit }: ActionContext<SocketState, RootState>, receiptProps: any) {
    if (receiptProps.isSave) {
      commit('pos/removeClientFromSavedReceipt', receiptProps);
    } else {
      commit('pos/removeClientFromReceipt', receiptProps);
      commit('pos/removeClientFromActualOrder', receiptProps);
    }
  },
  socketCreateSavedReceipt({ commit }: ActionContext<SocketState, RootState>, savedReceipt: any) {
    commit('pos/createSavedReceipt', savedReceipt);
    commit('pos/incrementSavedReceiptsCount');
  },
  socketRefundProducts({ commit }: ActionContext<SocketState, RootState>, receiptProps: any) {
    commit('pos/refundProducts', receiptProps);
  },
  socketRefundOrder({ commit }: ActionContext<SocketState, RootState>, { id }: { id: string }) {
    commit('pos/refundOrder', id);
  },
  socketCreateReceipt({ commit }: ActionContext<SocketState, RootState>, receiptProps: any) {
    if (receiptProps.typeOfPayment) {
      commit('pos/createReceipt', receiptProps);
    }

    commit('takeAway/createTakeAway', receiptProps);
    commit('pos/addActualOrder', receiptProps);
    commit('notice/createNewOrderNotice', {
      id: v4(),
      created: new SystemDate(),
      type: 'ORDER_CREATED',
      status: 1,
      entity: receiptProps,
    });
  },
  socketCreateNotPrintedOrder({ commit }: ActionContext<SocketState, RootState>, receiptProps: any) {
    commit('pos/createReceipt', receiptProps);
    commit('pos/removeSavedReceipt', receiptProps.id);
  },
  socketCreatePrintedOrder({ commit }: ActionContext<SocketState, RootState>, receiptProps: any) {
    commit('pos/updateNotPrintedReceipt', receiptProps);
    commit('takeAway/createTakeAway', receiptProps);
    commit('notice/createNewOrderNotice', {
      id: v4(),
      created: new SystemDate(),
      type: 'ORDER_CREATED',
      status: 1,
      entity: receiptProps,
    });
  },
  socketIssueOrder({ commit }: ActionContext<SocketState, RootState>, { id }: { id: string }) {
    commit('pos/removeActualOrder', id);
    commit('pos/decrementActualOrdersCount');
  },

  socketCreateCategory({ commit }: ActionContext<SocketState, RootState>, productProps: any) {
    commit('menu/createCategory', productProps);
  },
  socketRenameCategory({ commit }: ActionContext<SocketState, RootState>, productProps: any) {
    commit('menu/renameCategory', productProps);
  },
  socketRemoveCategory({ commit }: ActionContext<SocketState, RootState>, { id }: { id: string }) {
    commit('menu/removeCategoryWithoutProducts', id);
  },
  socketPurgeCategory({ commit }: ActionContext<SocketState, RootState>, { id }: { id: string }) {
    commit('menu/removeCategoryWithProducts', id);
  },
  socketToggleCategoryVisibilityInPos({ commit }: ActionContext<SocketState, RootState>, { id }: { id: string }) {
    commit('menu/toggleCategoryVisibilityInPos', id);
  },
  socketToggleCategoryVisibilityInWidget({ commit }: ActionContext<SocketState, RootState>, { id }: { id: string }) {
    commit('menu/toggleCategoryVisibilityInWidget', id);
  },
  socketCreateProduct({ commit }: ActionContext<SocketState, RootState>, productProps: ProductProps) {
    commit('menu/createProduct', productProps);
    commit(
      'storehouse/createProduct',
      {
        maxRemainder: 100,
        remainder: 0,
        ...productProps,
        id: productProps.projectProduct.id,
      },
    );
  },
  socketEditProduct({ commit }: ActionContext<SocketState, RootState>, productProps: ProductProps) {
    commit('menu/editProduct', productProps);
    commit('storehouse/editProduct', productProps);
  },
  socketEditStorehouseProduct({ commit }: ActionContext<SocketState, RootState>, productProps: ProductProps) {
    commit('storehouse/editStorehouseProduct', {
      ...productProps,
      maxCount: productProps.maxRemainder,
    });
  },
  socketRemoveProduct({ commit }: ActionContext<SocketState, RootState>, productProps: ProductProps) {
    commit('menu/removeProduct', productProps);
    commit('storehouse/removeProduct', productProps.projectProductId);
  },
  socketToggleIsFavoriteProduct({ commit }: ActionContext<SocketState, RootState>, productProps: ProductProps) {
    commit('menu/toggleIsFavoriteProduct', productProps);
  },
  socketToggleProductVisibility({ commit }: ActionContext<SocketState, RootState>, productProps: ProductProps) {
    commit('menu/toggleProductVisibility', productProps);
  },
  socketToggleProductVisibilityInPos({ commit }: ActionContext<SocketState, RootState>, productProps: ProductProps) {
    commit('menu/toggleProductVisibilityInPos', productProps);
  },
  socketCreateIngredient({ commit }: ActionContext<SocketState, RootState>, ingredientProps: IngredientProps) {
    commit('menu/createIngredient', ingredientProps);
    commit(
      'storehouse/createIngredient',
      {
        maxRemainder: 100,
        remainder: 0,
        ...ingredientProps,
        id: ingredientProps.projectIngredient.id,
      },
    );
  },
  socketEditIngredient({ commit }: ActionContext<SocketState, RootState>, ingredientProps: IngredientProps) {
    commit('menu/editIngredient', ingredientProps);
    commit('storehouse/editIngredient', ingredientProps);
  },
  socketEditStorehouseIngredient({ commit }: ActionContext<SocketState, RootState>, ingredientProps: IngredientProps) {
    commit('storehouse/editStorehouseIngredient', {
      ...ingredientProps,
      maxCount: ingredientProps.maxRemainder,
    });
  },
  socketRemoveIngredient({ commit }: ActionContext<SocketState, RootState>, ids: any) {
    commit('menu/removeIngredient', ids.id);
    commit('storehouse/removeIngredient', ids.projectIngredientId);
  },
  socketCreateTakeAwayOrder({ commit }: ActionContext<SocketState, RootState>, receiptProps: any) {
    commit('pos/createReceipt', receiptProps);
    commit('pos/addActualOrder', receiptProps);
    commit('takeAway/createTakeAway', receiptProps);
    commit('notice/createNewOrderNotice', {
      id: v4(),
      created: new SystemDate(),
      type: 'ORDER_CREATED',
      status: 1,
      entity: receiptProps,
    });
  },
  socketCancelTakeAwayOrder({ commit }: ActionContext<SocketState, RootState>, { id }: { id: string }) {
    commit('pos/removeActualOrder', id);
  },
  socketRemoveTakeAwayOrder({ commit }: ActionContext<SocketState, RootState>, { id, isSave }: any) {
    if (isSave) {
      commit('pos/removeSavedReceipt', id);
    } else {
      commit('pos/removeActualOrder', id);
    }

    commit('takeAway/removeTakeAway', id);
    commit('pos/removeReceipt', id);
  },
  socketTakeTakeAwayOrder({ commit }: ActionContext<SocketState, RootState>, takeAwayProps: TakeAwayProps) {
    commit('takeAway/takeOrder', takeAwayProps);
    commit('pos/takeOrder', takeAwayProps);
    commit('pos/takeActualOrder', takeAwayProps);
    commit('notice/takeOrder', takeAwayProps);
  },
  socketRefuseTakeAwayOrder({ rootState, commit }: any, { id }: { id: string }) {
    commit('takeAway/refuseOrder', id);
    commit('pos/refuseOrder', id);
    commit('pos/refuseActualOrder', id);
    commit('notice/refuseOrder', id);
  },
  socketDoneTakeAwayOrder({ commit }: ActionContext<SocketState, RootState>, { id }: { id: string }) {
    commit('takeAway/doneOrder', id);
    commit('pos/doneOrder', id);
    commit('pos/doneActualOrder', id);
    commit('notice/doneOrder', id);
    commit('pos/incrementActualOrdersCount');
  },
  socketUndoneTakeAwayOrder({ commit }: ActionContext<SocketState, RootState>, { id }: { id: string }) {
    commit('takeAway/undoneOrder', id);
    commit('pos/undoneOrder', id);
    commit('pos/undoneActualOrder', id);
    commit('pos/decrementActualOrdersCount');
  },
  socketDoneOrderProduct({ commit }: ActionContext<SocketState, RootState>, payload: any) {
    commit('takeAway/doneOrderProduct', payload);
  },
  socketUndoneOrderProduct({ commit }: ActionContext<SocketState, RootState>, payload: any) {
    commit('takeAway/undoneOrderProduct', payload);
  },
  socketCreateDeviceKassa({ commit }: ActionContext<SocketState, RootState>, payload: any) {
    const kassa = new DeviceKassa(payload);

    commit('settings/device/createDeviceKassa', kassa);
    commit('pos/kassa/setPos', kassa);
  },
  socketRemoveDeviceKassa({ commit }: ActionContext<SocketState, RootState>, { id }: { id: string }) {
    commit('settings/device/removeDeviceKassa', id);
    commit('pos/kassa/removeDeviceKassa', id);
  },
  socketAuthorizeDeviceKassa({ commit }: ActionContext<SocketState, RootState>, payload: any) {
    commit('settings/device/authorizePosDevice', payload);
  },
  socketEditDeviceKassa({ commit }: ActionContext<SocketState, RootState>, payload: any) {
    commit('settings/device/editDeviceKassa', payload);
    commit('pos/kassa/editDeviceKassa', payload);
  },
  socketCreateBuyXGetY({ commit, rootState }: ActionContext<SocketState, any>, payload: Promotion) {
    if (rootState.place.selectedPlace && payload.placeIds.includes(rootState.place.selectedPlace.id)) {
      commit('settings/promotion/createBuyXGetY', payload);
    }
  },
  socketRemoveBuyXGetY({ commit, rootState }: ActionContext<SocketState, any>, { id }: { id: string }) {
    commit('settings/promotion/removeBuyXGetY', id);
  },
  socketCreateClient({ commit }: ActionContext<SocketState, RootState>, clientProps: ClientProps) {
    commit('clients/addClient', Client.createFromRequest(clientProps));
  },
  socketEditClient({ commit }: ActionContext<SocketState, RootState>, clientProps: ClientProps) {
    commit('clients/editClient', clientProps);
  },
  socketEditPlace({ commit }: ActionContext<SocketState, RootState>, props: any) {
    commit('place/editPlace', props);
  },
  socketRemoveClient({ commit }: ActionContext<SocketState, RootState>, { id }: { id: string }) {
    commit('clients/removeClient', id);
  },
  socketFavoriteClient({ commit }: ActionContext<SocketState, RootState>, { id }: { id: string }) {
    commit('clients/makeClientFavorite', id);
  },
  socketBannedClient({ commit }: ActionContext<SocketState, RootState>, { id }: { id: string }) {
    commit('clients/makeClientBanned', id);
  },
  socketNormalClient({ commit }: ActionContext<SocketState, RootState>, { id }: { id: string }) {
    commit('clients/makeClientNormal', id);
  },
  socketCreateStaffPinCode({ commit }: ActionContext<SocketState, RootState>, params: { id: string }) {
    commit('settings/staff/makeStaffWithPinCode', params);
    commit('user/makeStaffWithPinCode', params);
  },
  socketCreateStaff({ commit }: ActionContext<SocketState, RootState>, userProps: UserProps) {
    const user = new User(userProps);

    commit('settings/staff/addStaff', user);
  },
  socketEditStaff({ commit }: ActionContext<SocketState, RootState>, userProps: UserProps) {
    commit('settings/staff/editStaff', userProps);
    commit('user/editStaff', userProps);
  },
  socketCreateMessage({ commit }: ActionContext<SocketState, RootState>, messageProps: ClientChatProps) {
    commit('clients/createMessage', messageProps);
  },
  socketCreateReview({ commit }: ActionContext<SocketState, RootState>, payload: any) {
    commit('takeAway/createReview', payload);
    commit('clients/createReview', payload);
  },
  socketCompleteStorehouseTransaction({ commit }: ActionContext<SocketState, RootState>, payload: any) {
    commit('storehouse/completeTransaction', payload);
  },
  socketCancelStorehouseTransaction({ commit }: ActionContext<SocketState, RootState>, payload: any) {
    commit('storehouse/cancelTransaction', payload);
  },
  socketCreateStorehouseTransaction({ commit }: ActionContext<SocketState, RootState>, payload: any) {
    commit('storehouse/createTransaction', payload);
  },
  socketWriteOffProducts({ commit }: ActionContext<SocketState, RootState>, payload: any) {
    commit('storehouse/writeOffProducts', payload.product);
    commit('storehouse/writeOffIngredients', payload.ingredient);
  },
  socketRemoveStaff({ commit, dispatch }: ActionContext<SocketState, RootState>, { id }: any) {
    commit('settings/staff/removeStaff', id);
    dispatch('user/removeStaff', id);
  },
  socketReadReviews({ commit }: ActionContext<SocketState, RootState>, { id, count }: any) {
    commit('clients/editClient', { id, unreadReviews: []});
    commit('clients/decreaseSectionNotification', count);
  },
  socketPlaceShowBlockedInformation({ commit, dispatch }: ActionContext<SocketState, RootState>, props: any) {
    commit('place/setShowAlmostBlockedInformation', props);
  },
  socketPlaceBlocked({ commit, dispatch }: ActionContext<SocketState, RootState>, props: any) {
    commit('place/setShowBlockedInformation', props);
  },
};

const mutations: MutationTree<SocketState> = {};

export default {
  namespaced,
  state,
  getters,
  actions,
  mutations,
};
